/**
 * configStore — Phase B (E.21.37)
 *
 * SPA-facing settings fetched from the backend's /api/config route.
 * Currently:
 *   - autoRebuildOnSave: boolean | null (null = not yet fetched)
 *
 * Mirrors tenantStore.ts:1-55 — idempotency guard, error → field stays
 * null, fetch via services/api.ts api.get (auto-supplies Bearer JWT).
 *
 * Why a separate store from tenantStore: tenantStore is explicitly
 * single-purpose for tenant identity per Plan E §E.6 D.G3 reviewer
 * concern (see tenantStore.ts:1-22 doc). Mixing UI configuration into
 * that contract would dilute the single-source-of-truth semantics. The
 * `One source of truth` rule favors a new store for distinct concerns.
 *
 * Race-safe default: `autoRebuildOnSave === null` until fetchConfig
 * resolves. The `useAutoRebuildOnSave` hook MUST treat null as "no
 * rebuild" so a Cmd+S in the 50ms before /api/config returns does not
 * silently bypass the user's tenant config (e.g. a tenant with
 * AUTO_REBUILD_ON_SAVE=false would otherwise see one phantom rebuild on
 * the first save before the flag arrives).
 */
import { create } from 'zustand';
import { api } from '../services/api';

interface ConfigState {
  autoRebuildOnSave: boolean | null;
  /**
   * mm_post tenant toggle (2026-07-13) — whether this tenant's agents may
   * post to Mattermost via vibe_mm_post. Server-side enforced; this field
   * is the SPA read/flip surface. null = not yet fetched.
   */
  agentMmPostEnabled: boolean | null;
  isLoading: boolean;
  error: Error | null;
  fetchConfig: () => Promise<void>;
  /**
   * Phase B.1a (E.21.39) — runtime toggle. PATCHes /api/config which
   * persists to /workspace/.vibe-config.json so the override survives
   * backend restart. State is updated from the server's response, not
   * the input arg (server is source of truth).
   *
   * On error: state.error populated, autoRebuildOnSave UNCHANGED so the
   * SPA's useAutoRebuildOnSave hook continues to honor whatever the
   * server still considers authoritative.
   */
  setAutoRebuildOnSave: (value: boolean) => Promise<void>;
  /** Same wire + semantics as setAutoRebuildOnSave, for the mm_post toggle. */
  setAgentMmPostEnabled: (value: boolean) => Promise<void>;
  reset: () => void;
}

export const useConfigStore = create<ConfigState>((set, get) => ({
  autoRebuildOnSave: null,
  agentMmPostEnabled: null,
  isLoading: false,
  error: null,

  fetchConfig: async () => {
    // Idempotency guard — mirrors tenantStore.ts:44. React StrictMode
    // double-invokes effects in dev. A second concurrent fetch is benign
    // (same response) but the guard makes the "fetch once" contract
    // explicit and avoids redundant network traffic.
    if (get().autoRebuildOnSave !== null || get().isLoading) return;
    set({ isLoading: true, error: null });
    try {
      const data = await api.get<{
        auto_rebuild_on_save?: boolean;
        agent_mm_post_enabled?: boolean;
      }>('/config');
      set({
        autoRebuildOnSave: data.auto_rebuild_on_save ?? null,
        agentMmPostEnabled: data.agent_mm_post_enabled ?? null,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      set({
        autoRebuildOnSave: null,
        agentMmPostEnabled: null,
        isLoading: false,
        error: err as Error,
      });
    }
  },

  setAutoRebuildOnSave: async (value) => {
    try {
      const data = await api.patch<{ auto_rebuild_on_save?: boolean }>(
        '/config',
        { auto_rebuild_on_save: value },
      );
      // Server response is source of truth — read autoRebuildOnSave back
      // from it rather than trusting the input arg. If a future server
      // policy clamps the value or fails server-side validation, the SPA
      // shows the truth.
      set({
        autoRebuildOnSave: data.auto_rebuild_on_save ?? null,
        error: null,
      });
    } catch (err) {
      // Defensive: leave autoRebuildOnSave unchanged. Mirrors
      // projectStore.saveFile's no-silent-failure discipline (Phase A).
      set({ error: err as Error });
    }
  },

  setAgentMmPostEnabled: async (value) => {
    try {
      const data = await api.patch<{ agent_mm_post_enabled?: boolean }>(
        '/config',
        { agent_mm_post_enabled: value },
      );
      // Server response is source of truth — a failed persist must never
      // render the toggle ON while the backend still denies vibe_mm_post.
      set({
        agentMmPostEnabled: data.agent_mm_post_enabled ?? null,
        error: null,
      });
    } catch (err) {
      set({ error: err as Error });
    }
  },

  reset: () =>
    set({
      autoRebuildOnSave: null,
      agentMmPostEnabled: null,
      isLoading: false,
      error: null,
    }),
}));
